import React from 'react';
import {
    Typography,
    IconButton,
} from '@material-ui/core';
import {
    ArrowBackIos,
    Fullscreen,
    FullscreenExit,
    Pause,
    PlayArrow,
    Settings,
    Stop,
} from '@material-ui/icons';
import EditIcon from '@material-ui/icons/Edit';
import { Box, Tooltip } from "@mui/material";
import { tUi } from '../../../../../../../utils/dq_lc';
import { V2_SUPPORTED_SLIDE_LAYOUTS } from '../../../../utils/LearnTab/preview.constants';
import { getSlideLayout } from '../../../../utils/LearnTab/preview.utils';
import LanguageSelector from './LanguageSelector.comp';

const TopBar = ({
    tts,
    editor,
    nav,
    data,
    currentSlide = nav?.currentSlide ?? 0,
    totalSlides = nav?.currentSegmentSlides?.length ?? 0,
    sessionTitle = data?.selectedSession?.lesson_title ?? '',
    isMobile = false,
    embedded = false,
    onBackToSessions,
    speaking = tts?.speaking ?? false,
    paused = tts?.paused ?? false,
    onToggleSpeak = tts ? () => {
        if (!tts.speaking) {
            tts.setTtsAuto(true);
            tts.startSpeech();
        } else {
            tts.pauseSpeech();
        }
    } : undefined,
    onStopSpeak = tts ? () => {
        tts.setTtsAuto(false);
        tts.stopSpeech();
    } : undefined,
    onOpenTtsSettings = tts ? (e) => tts.setTtsSettingsAnchor(e.currentTarget) : undefined,
    readOnly = false,
    isEditMode = editor?.isEditMode ?? false,
    canEdit: explicitCanEdit,
    onToggleEdit = editor?.handleEditButtonClick,
    editTooltip: explicitEditTooltip,
    availableSlideLanguages = data?.availableSlideLanguages ?? [],
    currentDisplayLanguage = data?.currentDisplayLanguage ?? 'en-US',
    onSelectLanguage = editor?.handleSelectLanguage || data?.handleSelectLanguage,
    focusMode = false,
    onToggleFocusMode,
    activeSegment = nav?.activeSegment ?? '',
    uiLocale = data?.uiLocale ?? 'en-US',
}) => {
    const isAssessment = activeSegment === '__PRE_ASSESSMENT__' || activeSegment === '__POST_ASSESSMENT__';
    const currentLayout = getSlideLayout(nav?.currentSessionSlide);
    const canEdit = explicitCanEdit !== undefined
        ? explicitCanEdit
        : (!readOnly && nav?.currentSessionSlide && (V2_SUPPORTED_SLIDE_LAYOUTS.includes(currentLayout) || nav.currentSessionSlide.slide_type || nav.currentSessionSlide.question));

    const editTooltip = explicitEditTooltip || (
        isEditMode
            ? tUi('preview.tooltip.exitEditMode', uiLocale)
            : availableSlideLanguages.length <= 1 && availableSlideLanguages.includes('en-US')
                ? tUi('preview.tooltip.editNotAvailableEnglish', uiLocale)
                : (data?.currentDisplayLanguage === 'en-US' && availableSlideLanguages.length > 1)
                    ? tUi('preview.tooltip.editToggleLanguage', uiLocale)
                    : tUi('preview.tooltip.editSlideContent', uiLocale)
    );

    return (
        <>

            {/* Mobile embedded: back-to-sessions nav */}
            {embedded && isMobile && onBackToSessions && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1.5, borderBottom: '1px solid #f0f0f0', width: '100%', flexShrink: 0 }}>
                    <IconButton onClick={onBackToSessions} size="small" style={{ color: '#ef6528', backgroundColor: '#fff0e8', padding: '4px', borderRadius: '8px' }}>
                        <ArrowBackIos style={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Sessions
                    </Typography>
                </Box>
            )}

            {/* Header row: slide counter | session title | preview tools */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                width: '100%',
                margin: '12px auto',
                marginBottom: isMobile ? '8px' : '12px',
                minHeight: '46px',
            }}>
                <Typography style={{
                    color: '#777',
                    fontWeight: 700,
                    fontSize: isMobile ? '14px' : '18px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}>
                    <span style={{ color: '#ED6628' }}>{currentSlide + 1} </span>/ {totalSlides}
                </Typography>

                <Typography variant="h4" style={{
                    color: '#000000',
                    fontWeight: '800',
                    textAlign: 'center',
                    fontSize: isMobile ? '16px' : 'clamp(22px, 3vw, 26px)',
                    margin: 0,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {sessionTitle}
                </Typography>

                {/* Preview tools: language selector, read aloud, voice settings, edit slide, focus mode */}
                <Box style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    padding: '3px 6px',
                    borderRadius: '999px',
                    backgroundColor: '#fff',
                    border: '1px solid #e6e9ef',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    flexShrink: 0,
                }}>
                    <LanguageSelector
                        availableLanguages={availableSlideLanguages}
                        currentLanguage={currentDisplayLanguage}
                        onSelectLanguage={onSelectLanguage}
                    />

                    <Tooltip title={speaking && !paused ? 'Pause reading' : 'Read this slide aloud'} arrow>
                        <IconButton
                            size="small"
                            onClick={onToggleSpeak}
                            style={{ color: '#ED6628', padding: 6 }}
                        >
                            {speaking && !paused ? <Pause style={{ fontSize: 22 }} /> : <PlayArrow style={{ fontSize: 22 }} />}
                        </IconButton>
                    </Tooltip>
                    {speaking && (
                        <Tooltip title="Stop reading" arrow>
                            <IconButton
                                size="small"
                                onClick={onStopSpeak}
                                style={{ color: '#ED6628', padding: 6 }}
                            >
                                <Stop style={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Voice settings" arrow>
                        <IconButton
                            size="small"
                            aria-label="TTS settings"
                            onClick={onOpenTtsSettings}
                            style={{ color: '#6b7280', padding: 6 }}
                        >
                            <Settings style={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                    {!readOnly && canEdit && (
                        <Tooltip title={editTooltip} arrow>
                            <IconButton
                                size="small"
                                onClick={onToggleEdit}
                                style={{
                                    color: '#fff',
                                    backgroundColor: isEditMode ? '#0097b2' : '#EE6D29',
                                    padding: 6,
                                    margin: '0 2px',
                                }}
                            >
                                <EditIcon style={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {embedded && onToggleFocusMode && (
                        <Tooltip title={focusMode ? 'Exit focus mode' : 'Focus mode — hide menus to see more of the slide'} arrow>
                            <IconButton
                                size="small"
                                onClick={onToggleFocusMode}
                                style={{ color: focusMode ? '#0097b2' : '#6b7280', padding: 6 }}
                            >
                                {focusMode ? <FullscreenExit style={{ fontSize: 22 }} /> : <Fullscreen style={{ fontSize: 22 }} />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Assessment title header banner */}
            {isAssessment && (
                <Typography variant="h5" style={{
                    color: '#fe696e',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginBottom: '40px !important',
                    marginTop: '10px',
                    fontSize: 'clamp(22px, 3.2vw, 28px)'
                }}>
                    {activeSegment === '__PRE_ASSESSMENT__' ? tUi('preview.preAssessment', uiLocale) : tUi('preview.postAssessment', uiLocale)}
                </Typography>
            )}
        </>
    );
};

export const PreviewTopBar = TopBar;
export { TopBar };
export default TopBar;
